/**
 * PKCE (Proof Key for Code Exchange, RFC 7636) helpers for the OIDC
 * Authorization Code flow against Medicus IAM (ADR-0021, ADR-0041).
 *
 * - `code_verifier`: 43-128 char URL-safe random string.
 * - `code_challenge`: BASE64URL(SHA256(code_verifier)).
 * - `state`: opaque random string the SPA round-trips through IAM to
 *   defeat CSRF on the redirect-back leg.
 *
 * The frontend NEVER holds the IAM client secret. The verifier lives in
 * `sessionStorage` only for the duration of the round trip and is cleared
 * once the backend exchange succeeds (per readiness Q-2 IAM contract).
 */

const VERIFIER_LENGTH = 64; // 43-128 inclusive per RFC 7636 §4.1.

const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";

/**
 * Encode raw bytes as base64url (no padding, `+`→`-`, `/`→`_`) per RFC 4648 §5.
 * The inverse of `atob` after the standard base64 → base64url translation.
 */
export function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i] as number);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomString(length: number): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  let out = "";
  for (let i = 0; i < length; i += 1) {
    // Index into the RFC 7636 §4.1 unreserved-character alphabet (66 chars).
    out += ALPHABET[(bytes[i] as number) % ALPHABET.length];
  }
  return out;
}

/** Generate a fresh PKCE `code_verifier`. RFC 7636 §4.1: 43-128 unreserved chars. */
export function generateCodeVerifier(): string {
  return randomString(VERIFIER_LENGTH);
}

/** Compute the `code_challenge` for a verifier using S256 (RFC 7636 §4.2). */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Generate an opaque state token for CSRF protection on the redirect-back leg. */
export function generateState(): string {
  return randomString(32);
}
